import { decodeJwt } from '@stayon/auth-core';
import type { SupportSessionApi } from '@/shared/api/supportSessionEndpoints';
import type { SupportSessionAccessResponse, SupportSessionResponse } from '@/shared/api/types';
import { supportEndReason } from './supportFailure';
import type { SupportEndReason, SupportNotice } from './supportNotices';
import { supportSessionStorage } from './supportSessionStorage';

export function viewOf(access: SupportSessionAccessResponse): SupportSessionResponse {
  const { sessionId, workspaceId, workspaceName, workspaceSlug, startedAt, expiresAt } = access;
  return { sessionId, workspaceId, workspaceName, workspaceSlug, startedAt, expiresAt };
}

/** Ein Token, das schon bis zum Sitzungsende gilt, lässt sich nicht verlängern. */
export function lastsUntilSessionEnd(token: string, expiresAt: string): boolean {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' && exp * 1000 >= Date.parse(expiresAt) - 1_000;
}

interface ResumeOptions {
  api: SupportSessionApi;
  /** Übernimmt die Antwort, wenn sie zur Heimatsitzung passt; sonst `false`. */
  adopt: (access: SupportSessionAccessResponse) => boolean;
  signedIn: () => boolean;
  notify: (notice: SupportNotice) => void;
}

/**
 * Nach dem Neuladen kehrt nur die eigene, noch laufende Sitzung mit genau der abgelegten Id
 * zurück. Ohne Antwort des Servers bleibt die Id liegen, damit der nächste Versuch sie findet.
 */
export async function resumeSupportSession({
  api,
  adopt,
  signedIn,
  notify,
}: ResumeOptions): Promise<void> {
  const storedId = supportSessionStorage.read();
  if (!storedId) return;
  let running: SupportSessionResponse | null = null;
  let reason: SupportEndReason = 'closed';
  try {
    running = await api.current();
    if (running?.sessionId === storedId && adopt(await api.issueToken(storedId))) return;
  } catch (error) {
    const failure = await supportEndReason(error, running?.expiresAt);
    if (failure === null) throw error;
    reason = failure;
  }
  supportSessionStorage.write(null);
  if (!signedIn()) return;
  notify({
    reason,
    workspaceName: running?.sessionId === storedId ? running.workspaceName : null,
  });
}
